import React, { useEffect, useState, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { apiUrl, PORT } from "../../environment";
import { AiFillDelete } from "react-icons/ai";
import "./AddMedia2.css";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ProviderService, {
  fetchSubCategoryOfCategory,
  getCategory,
} from "../services/providerService";
import { formikAddMedia } from "./validation";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { Autocomplete, TextField } from "@mui/material";
import { handleDelete } from "../../utils/Alerts";
import Loader from "../../utils/Loader";
import InputAdornment from "@mui/material/InputAdornment";
import BackButton from "../../utils/BackButton";

function AddMedia() {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const subCategoryIconInputRef = useRef(null);
  const { media } = location.state || {};
  const [loading, setLoading] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [filePreview, setFilePreview] = useState([]);
  const [subCategoryFilePreview, setSubCategoryFilePreview] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [updatedData, setUpdatedData] = useState([]);
  const [mediaData, setMediaData] = useState(media || {});

  const formik = useFormik({
    ...formikAddMedia,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const formDataToSend = new FormData();
        formDataToSend.append("title", values.title);
        formDataToSend.append("shortDescription", values.shortDescription);
        formDataToSend.append("detailsDescription", values.detailsDescription);
        formDataToSend.append("category", values.category);
        formDataToSend.append("subCategory", values.subCategory);
        formDataToSend.append("mediaType", values.mediaType);

        values.files.forEach((file, index) => {
          formDataToSend.append("files", file);
        });

        if (Array.isArray(values.subCategoryIcon)) {
          values.subCategoryIcon.forEach((file, index) => {
            formDataToSend.append("subCategoryIcon", file);
          });
        } else {
          console.error(
            "subCategoryIcon is not an array:",
            values.subCategoryIcon
          );
        }

        if (media) {
          const res = await ProviderService.updateMedia(
            formDataToSend,
            media._id
          );

          if (res.status === "SUCCESS") {
            setLoading(false);

            toast.success(res.message);
            setTimeout(() => {
              navigate("/admin/media-management");
            }, 80);
          } else {
            setLoading(false);

            toast.error(res.data.message);
          }
        } else {
          setLoading(true);

          const res = await ProviderService.addMedia(formDataToSend);

          if (res.status === "SUCCESS") {
            setLoading(false);

            toast.success(res.data.message);
            setTimeout(() => {
              navigate("/admin/media-management");
            }, 80);
          } else {
            setLoading(false);

            toast.error(res.data.message);
          }
        }
      } catch (error) {
        setLoading(false);
        toast.error("Failed!");
      }
    },
  });

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);

    formik.setFieldValue("files", files);

    const checkImageValid = (filesArray) => {
      const validTypes = ["image/jpeg", "image/png"];

      // Validate image files
      const validFiles = filesArray.filter((file) =>
        validTypes.includes(file.type)
      );
      const invalidFiles = filesArray.filter(
        (file) => !validTypes.includes(file.type)
      );

      if (invalidFiles.length > 0) {
        toast.error(
          "Invalid file type(s) detected! Only JPG and PNG images are allowed.!"
        );

        if (fileInputRef.current) {
          fileInputRef.current.value = ""; // Clear input field
        }
        return false;
      } else {
        return true;
      }
    };

    const checkVideoValid = (filesArray) => {
      const validTypes = ["video/mp4", "video/webm"];

      // Validate video files
      const validFiles = filesArray.filter((file) =>
        validTypes.includes(file.type)
      );
      const invalidFiles = filesArray.filter(
        (file) => !validTypes.includes(file.type)
      );
      if (invalidFiles.length > 0) {
        toast.error(
          "Invalid file type detected! Only MP4 and WebM videos are allowed.!"
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return false;
      } else {
        return true;
      }
    };

    if (
      formik.values.mediaType === "image" &&
      files.length > 0 &&
      checkImageValid(files)
    ) {
      const previewUrls = Array.from(files).map((file) =>
        URL.createObjectURL(file)
      );
      setFilePreview((prevPreview) => [...prevPreview, ...previewUrls]); // Update preview list
    }

    if (
      formik.values.mediaType === "video" &&
      files.length > 0 &&
      checkVideoValid(files)
    ) {
      const videoURLs = files.map((file) => URL.createObjectURL(file));
      setVideoPreviews(videoURLs);
    }
  };

  const handleFileChangeForSubCategory = (event) => {
    const files = Array.from(event.target.files);

    formik.setFieldValue("subCategoryIcon", files);

    const checkImageValid = (filesArray) => {
      const validTypes = ["image/jpeg", "image/png"];

      // Validate image files
      const validFiles = filesArray.filter((file) =>
        validTypes.includes(file.type)
      );
      const invalidFiles = filesArray.filter(
        (file) => !validTypes.includes(file.type)
      );

      if (invalidFiles.length > 0) {
        toast.error(
          "Invalid file type(s) detected! Only JPG and PNG images are allowed.!"
        );

        if (subCategoryIconInputRef.current) {
          fileInputRef.current.value = ""; // Clear input field
        }
        return false;
      } else {
        return true;
      }
    };

    if (checkImageValid(files)) {
      const previewUrls = Array.from(files).map((file) =>
        URL.createObjectURL(file)
      );
      setSubCategoryFilePreview((prevPreview) => [
        ...prevPreview,
        ...previewUrls,
      ]); // Update preview list
    }
  };

  // Handle file deletion
  const handleDeleteFile = (index, type) => {
    if (type === "image") {
      const updatedFiles = [...filePreview];
      updatedFiles.splice(index, 1);
      formik.setFieldValue("files", updatedFiles);

      const updatedPreviews = [...filePreview];
      updatedPreviews.splice(index, 1);
      setFilePreview(updatedPreviews);
    }

    if (type === "video") {
      const updatedVideos = [...videoPreviews];
      updatedVideos.splice(index, 1);
      setVideoPreviews(updatedVideos);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Clear input field
    }
  };

  const handleDeleteFileFromDB = async (data, index) => {
    // Create a copy of the media array to avoid direct mutation

    await handleDelete(
      `/user-service/media/${media._id}/deleteImage`,
      handleSuccess,
      { data, index },
      getDataBack
    );
    const updatedMedia = [...mediaData.mediaURL];
  };

  const getDataBack = async (data, index) => {
    const updatedMedia = [...mediaData.mediaURL];
    // Remove the image at the selected index
    updatedMedia.splice(index, 1);
    // Update the media state with the new media array
    setMediaData((prevMedia) => ({
      ...prevMedia,
      mediaURL: updatedMedia,
    }));
  };

  const handleSuccess = (response) => {
    if (response.status === "SUCCESS") {
      setUpdatedData(response.data);
      media.mediaURL = response.data.mediaURL;
    }
  };

  const checkMediaDetails = async () => {
    if (media) {
      formik.setValues({
        title: media.title || "",
        detailsDescription: media.description || "",
        category: media.category || "",
        subCategory: media.subCategory || "",
        mediaType: media.mediaType || "",
        files: media.mediaURL || [],
        oldFiles: media.mediaURL || [],
        subCategoryIcon: [media.subCategoryIcon] || [],
      });
    }
  };

  const getAcceptType = (mediaType) => {
    switch (mediaType) {
      case "audio":
        return "audio/*";
      case "video":
        return "video/*";
      case "image":
        return "image/*";
      // case "documents":
      //   return ".pdf,.txt";
      default:
        return "";
    }
  };

  const __getCategory = async () => {
    await getCategory({}, (data) => {
      setCategoryList(data || []);
    });
  };

  // Format the category data for the dropdown
  const categoryOptions = categoryList.map((item) => ({
    label: item.category, // The category name that will show in the dropdown
    value: item.category, // Use category's _id as the value
  }));

  useEffect(() => {
    __getCategory();
    getSubCategory(media?.category);
    checkMediaDetails();

    if (media) {
      setMediaData(media);
    }
  }, [media]);

  // ********************************** category ************************************************************

  const [subCategory, setSubCategory] = useState([]);
  const [subCategoryText, setSubCategoryText] = useState([]);

  const getSubCategory = async (category) => {
    if (!category) {
      return;
    }
    const result = await fetchSubCategoryOfCategory({ category: category });
    setSubCategory(result?.data?.subcategories);
    formik.setFieldValue(
      "subCategoryIcon",
      result?.data?.subcategories.icon ? [result?.data?.subcategories.icon] : ""
    );
  };

  // const MyEditor = () => {
  const [editorValue, setEditorValue] = useState("");
  const quillRef = useRef(null);

  const handleEditorChange = (value) => {
    setEditorValue(value);
  };

  // Handle Quill editor changes
  const handleQuillChange = (value) => {
    formik.setFieldValue("detailsDescription", value);
  };

  return (
    <div className="Dashboard-wrapper">
      {/* doctor-page */}
      {loading ? (
        <Loader />
      ) : (
        <div className="dashboard-insights">
          <BackButton />
          <Form className="form-block mt-2" onSubmit={formik.handleSubmit}>
            <div className="doctor-box">
              <Form.Group
                className=""
                controlId="formProfilePhoto"
              ></Form.Group>

              <div className="p-4">
                <div className="row mb-4">
                  <div className="col-md-4">
                    <div className="form-control-area">
                      <Form.Group
                        className="mb-3"
                        controlId="exampleForm.ControlInputFirstName"
                      >
                        <Form.Label className="details">
                          Title<span className="asterisk">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter title"
                          name="title"
                          keyField={"title"}
                          formik={formik}
                          value={formik.values.title}
                          onChange={(e) =>
                            formik.setFieldValue("title", e.target.value)
                          }
                          onBlur={formik.handleBlur}
                          isInvalid={
                            formik.touched.title && !!formik.errors.title
                          }
                        />
                        {formik.touched.title && formik.errors.title ? (
                          <div className="text-danger">
                            {formik.errors.title}
                          </div>
                        ) : null}
                      </Form.Group>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-control-area">
                      <Form.Group
                        className="mb-3"
                        controlId="exampleForm.ControlSelectCategory"
                      >
                        <Form.Label className="details">
                          Category <span className="asterisk">*</span>
                        </Form.Label>

                        <Autocomplete
                          options={categoryOptions} // The formatted options
                          getOptionLabel={(option) => option.label} // Specify the label for dropdown items
                          onChange={(event, value) => {
                            formik.setFieldValue(
                              "category",
                              value ? value.value : ""
                            );
                            getSubCategory(value ? value.value : "");
                          }}
                          value={
                            categoryOptions.find(
                              (option) =>
                                option.value === formik.values.category
                            ) || null
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label=""
                              placeholder="Select Category"
                              variant="outlined"
                              error={
                                formik.touched.category &&
                                Boolean(formik.errors.category)
                              }
                            />
                          )}
                        />

                        {formik.touched.category && formik.errors.category ? (
                          <div className="text-danger">
                            {formik.errors.category}
                          </div>
                        ) : null}
                      </Form.Group>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-control-area">
                      <Form.Group className="mb-2">
                        <Form.Label className="details">
                          Sub Category <span className="asterisk">*</span>
                        </Form.Label>

                        <Autocomplete
                          options={subCategory}
                          getOptionLabel={(option) =>
                            (typeof option === "string"
                              ? option
                              : option.name) || ""
                          }
                          value={
                            subCategory.find(
                              (option) =>
                                option.name === formik.values?.subCategory
                            ) ||
                            formik.values?.subCategory ||
                            null
                          }
                          onChange={(event, value) => {
                            formik.setFieldValue(
                              "subCategory",
                              value ? value.name : ""
                            );
                            formik.setFieldValue(
                              "subCategoryIcon",
                              value ? value.icon : ""
                            );
                          }}
                          renderOption={(props, option) => (
                            <li
                              {...props}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              {option.name}
                              <img
                                src={`${apiUrl}${PORT}/user-service/getimages/${option.icon}`}
                                alt="Preview"
                                className="file-preview-image"
                                style={{ maxWidth: "40px", marginLeft: "10px" }}
                              />
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select or Enter Sub Category"
                              variant="outlined"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: formik?.values?.subCategoryIcon
                                  ?.icon ? (
                                  <InputAdornment position="start">
                                    <img
                                      src={`${apiUrl}${PORT}/user-service/getimages/${formik.values.subCategoryIcon}`}
                                      alt="Selected Icon"
                                      style={{
                                        width: "40px",
                                        height: "40px",
                                        objectFit: "contain",
                                        borderRadius: "50%",
                                      }}
                                    />
                                  </InputAdornment>
                                ) : null,
                              }}
                              error={
                                formik.touched.subCategory &&
                                Boolean(formik.errors.subCategory)
                              }
                            />
                          )}
                        />

                        {formik.touched.subCategory &&
                          formik.errors.subCategory ? (
                          <div className="text-danger">
                            {formik.errors.subCategory}
                          </div>
                        ) : null}
                      </Form.Group>
                    </div>
                  </div>
                </div>
                <div className="row mb-2 mt-2">
                  <div className="col-12">
                    <div className="form-control-area">
                      <Form.Group
                        className="mb-3"
                        controlId="exampleForm.ControlInputMobile"
                      >
                        <Form.Label className="details">
                          Description<span className="asterisk">*</span>
                        </Form.Label>

                        <ReactQuill
                          style={{ height: "200px", color: "#6B7280" }}
                          className="mb-2"
                          placeholder="Enter detail descriptions."
                          name="detailsDescription"
                          value={formik.values.detailsDescription}
                          onChange={(value) =>
                            formik.setFieldValue("detailsDescription", value)
                          }
                          onBlur={() =>
                            formik.setFieldTouched("detailsDescription", true)
                          }
                          modules={{
                            toolbar: [
                              [{ header: [1, 2, 3, false] }],
                              ["bold", "italic", "underline"],
                              [{ list: "ordered" }, { list: "bullet" }],
                              ["link", "image", "video"], // Insert options
                              [{ align: [] }],
                              ["clean"],
                              [{ color: [] }],
                            ],
                          }}
                        />
                        {formik.touched.detailsDescription &&
                          formik.errors.detailsDescription ? (
                          <div className="text-danger mt-5">
                            {formik.errors.detailsDescription}
                          </div>
                        ) : null}
                      </Form.Group>
                    </div>
                  </div>
                </div>
                <br />

                <div className="row mb-4 mt-4 ">
                  <div className="col-md-4">
                    <div className="form-control-area">
                      <Form.Group
                        className="mb-3"
                        controlId="exampleForm.ControlSelectGender"
                      >
                        <Form.Label className="details">Media Type</Form.Label>
                        <Form.Select
                          className="selected-fields"
                          name="mediaType"
                          keyField={"mediaType"}
                          value={formik.values?.mediaType}
                          onChange={(e) =>
                            formik.setFieldValue("mediaType", e.target.value)
                          }
                          onBlur={formik.handleBlur}
                          isInvalid={
                            formik.touched?.mediaType &&
                            !!formik.errors?.mediaType
                          }
                        >
                          <option value="">Select Media Type</option>
                          {/* <option value="audio">Audio</option> */}
                          <option value="video">Video</option>
                          <option value="image">Images</option>
                          {/* <option value="documents">Documents (pdf, txt)</option> */}
                          {/* <option value="other">Others</option> */}
                        </Form.Select>
                        {formik.touched?.mediaType &&
                          formik.errors?.mediaType ? (
                          <div className="text-danger">
                            {formik.errors?.mediaType}
                          </div>
                        ) : null}
                      </Form.Group>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-control-area">
                      <Form.Group
                        className="mb-3"
                        controlId="exampleForm.ControlInputDOB"
                      >
                        <Form.Label className="details">
                          Upload Files
                        </Form.Label>
                        <Form.Control
                          id="files"
                          keyField={"files"}
                          name="files"
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept={getAcceptType(formik.values.mediaType)} // Dynamically set accept type
                          onChange={handleFileChange}
                          onBlur={formik.handleBlur}
                          disabled={formik.values.mediaType ? false : true}
                          isInvalid={
                            formik.touched.files && !!formik.errors.files
                          }
                        />
                        {formik.touched.files && formik.errors.files ? (
                          <div className="text-danger">
                            {formik.errors.files}
                          </div>
                        ) : null}
                      </Form.Group>
                    </div>
                  </div>
                </div>
                {/* Display selected files with previews */}
                {formik.values?.mediaType === "image" && (
                  <div className="file-preview-container">
                    {filePreview.map((file, index) => (
                      <div key={index} className="file-preview-item">
                        <img
                          src={file}
                          alt={`Preview ${index}`}
                          className="file-preview-image"
                          style={{ maxWidth: "100px", marginRight: "10px" }}
                        />

                        <AiFillDelete
                          style={{ cursor: "pointer" }}
                          className="text-danger delete-icon cursor"
                          onClick={() => handleDeleteFile(index, "image")}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {mediaData?.mediaType === "image" && (
                  <div className="file-preview-container">
                    {mediaData?.mediaURL?.map((file, index) => (
                      <div key={index} className="file-preview-item">
                        <img
                          src={`${apiUrl}${PORT}/user-service/getimages/${file}`}
                          alt={`Preview ${index}`}
                          className="file-preview-image"
                          style={{ maxWidth: "100px", marginRight: "10px" }}
                        />

                        <AiFillDelete
                          style={{ cursor: "pointer" }}
                          className="text-danger delete-icon  cursor"
                          onClick={() => handleDeleteFileFromDB(media, index)}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* display video preview  */}
                {formik.values?.mediaType === "video" && (
                  <div className="file-preview-container mt-2 mb-2">
                    {videoPreviews.map((file, index) => (
                      <div key={index} className="file-preview-item">
                        <video
                          controls
                          width="100"
                          height="70"
                          src={file}
                          style={{
                            border: "1px solid #ccc",
                            borderRadius: "5px",
                            marginRight: "10px",
                          }}
                        ></video>

                        <AiFillDelete
                          style={{ cursor: "pointer" }}
                          className="text-danger delete-icon cursor"
                          onClick={() => handleDeleteFile(index, "video")}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {mediaData?.mediaType === "video" && (
                  <div className="file-preview-container">
                    {mediaData?.mediaURL?.map((file, index) => (
                      <div key={index} className="file-preview-item">
                        <video
                          controls
                          width="100"
                          height="70"
                          src={`${apiUrl}${PORT}/user-service/getvideos/${file}`}
                          style={{
                            border: "1px solid #ccc",
                            borderRadius: "5px",
                            marginRight: "10px",
                          }}
                        ></video>

                        <AiFillDelete
                          style={{ cursor: "pointer" }}
                          className="text-danger delete-icon cursor"
                          onClick={() => handleDeleteFileFromDB(media, index)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="button-block">
              <Link to="/admin/media-management">
                <Button className="outlined-btn">Cancel</Button>
              </Link>
              {media ? (
                <Button type="submit" className="filled-btn">
                  Update
                </Button>
              ) : (
                <Button type="submit" className="filled-btn">
                  Save
                </Button>
              )}
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
export default AddMedia;
